import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import can from 'ember-can/helpers/can';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import { and } from 'ember-truth-helpers';
import SlideToggle from 'garaje/components/slide-toggle';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type VisitorsConfiguration from 'garaje/models/visitors-configuration';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface EmployeeSignOutDisabledSignature {
  Args: {
    visitorsConfiguration: VisitorsConfiguration;
  };
  Element: HTMLDivElement;
}

export default class EmployeeSignOutDisabled extends Component<EmployeeSignOutDisabledSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare abilities: AbilitiesService;

  @tracked showAllowConfirmModal = false;
  @tracked showPreventConfirmModal = false;

  get toggleDisabled(): boolean {
    return this.saveTask.isRunning || this.abilities.cannot('manage-employee-sign-out-disabled visitors-configuration');
  }

  onToggle = (checked: boolean): void => {
    if (checked) {
      this.showAllowConfirmModal = true;
    } else {
      this.showPreventConfirmModal = true;
    }
  };

  closeAllowModal = (): void => {
    this.showAllowConfirmModal = false;
  };

  closePreventModal = (): void => {
    this.showPreventConfirmModal = false;
  };

  confirmAllowTask = dropTask(async () => {
    await this.saveTask.perform(true);
    this.showAllowConfirmModal = false;
  });

  confirmPreventTask = dropTask(async () => {
    await this.saveTask.perform(false);
    this.showPreventConfirmModal = false;
  });

  saveTask = dropTask(async (value: boolean) => {
    const config = this.args.visitorsConfiguration;
    config.employeeSignOutEnabled = value;

    try {
      await config.save();
      this.flashMessages.showAndHideFlash(
        'success',
        value ? 'Hosts can now sign out visitors.' : 'Hosts can no longer sign out visitors.',
      );
    } catch (e) {
      config.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  <template>
    <SettingsPanel ...attributes data-test-employee-sign-out-disabled as |panel|>
      <panel.header
        @iconUri="/assets/images/host-sign-out.svg"
        @title="Allow hosts to sign out visitors"
        @description="Allow hosts to sign out their own visitors"
        as |header|
      >
        <header.controls>
          <SlideToggle
            data-test-employee-sign-out-disabled-toggle
            aria-disabled={{this.toggleDisabled}}
            @isChecked={{@visitorsConfiguration.employeeSignOutEnabled}}
            @isDisabled={{this.toggleDisabled}}
            @onClick={{this.onToggle}}
          />
        </header.controls>
      </panel.header>
    </SettingsPanel>

    {{#if (and this.showPreventConfirmModal (can "manage-employee-sign-out-disabled visitors-configuration"))}}
      <ConfirmationModal
        data-test-employee-sign-out-disabled-prevent-modal
        @title="Prevent hosts from signing out visitors"
        @ctaText="Confirm"
        @showTopRightCloseX={{true}}
        @onClose={{this.closePreventModal}}
        @handleTaskAction={{this.confirmPreventTask.perform}}
        @hideCancelButton={{true}}
        @containerClass="!max-w-[550px]"
      >
        <p class="mt-4">
          Hosts will no longer be able to sign out their visitors at this location.
        </p>

        <p class="mt-8">
          Front Desk Admins, Location Admins, and Global Admins can sign out visitors. If you need other roles to sign
          out visitors, you can configure that in
          <LinkTo @route="manage.roles">Roles and permissions.</LinkTo>
        </p>
      </ConfirmationModal>
    {{/if}}

    {{#if (and this.showAllowConfirmModal (can "manage-employee-sign-out-disabled visitors-configuration"))}}
      <ConfirmationModal
        data-test-employee-sign-out-disabled-allow-modal
        @title="Allow hosts to sign out visitors"
        @ctaText="Confirm"
        @showTopRightCloseX={{true}}
        @onClose={{this.closeAllowModal}}
        @handleTaskAction={{this.confirmAllowTask.perform}}
        @hideCancelButton={{true}}
        @containerClass="!max-w-[550px]"
      >
        <p class="mt-4">
          Hosts will be able to sign out their visitors at this location.
        </p>
      </ConfirmationModal>
    {{/if}}
  </template>
}
